import ZoomInIcon from "../icons/ZoomInIcon";
import { useWishlist } from "@/context/wishlist";
import { handleWishList } from "@/utils/wishlist";
import { Swiper, SwiperSlide } from "swiper/react";
import { useEffect, useMemo, useState } from "react";
import { Pagination } from "swiper/modules";
import ShareIcon from "../icons/ShareIcon";
import { validateImgSrc } from "@/utils/global";
import { getProductVariant } from "@/helpers/productDetails";
import { event_view_item } from "@/utils/gtm";
import NextImage from "../global/NextImage";
import { v4 as uuidv4 } from "uuid";
import InfoIcon from "../icons/InfoIcon";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";

const Share = dynamic(() => import("../share"));
const ImageLightBox = dynamic(() => import("../Lightbox"));
const AddToCompare = dynamic(() => import("./AddToCompare"));
const AddtoWishlist = dynamic(() => import("../global/buttons/AddtoWishlist"));

const Album = ({ product: parentProduct }) => {
  const { t } = useTranslation("product");
  const product = getProductVariant(parentProduct);
  const images = useMemo(() => {
    return product?._media_?.image || [];
  }, [product]);
  const { wishlist, dispatch } = useWishlist();
  const [previewImg, setPreviewImg] = useState(null);
  const [openFancybox, setOpenFancybox] = useState(false);

  useEffect(() => {
    setPreviewImg(product?.selectedConfigurationsImg || images.at(0)?.image);
  }, [images, product?.selectedConfigurationsImg]);

  const textLabels =
    product?.label?.length > 0 &&
    product?.label?.filter(
      (label) =>
        label?.status === "1" &&
        !label?.["frontend_settings"]?.image &&
        !!label?.["frontend_settings"]?.label_text
    );

  return (
    <div className="relative border rounded-md sm:p-3 col-span-full md:col-span-6 h-fit">
      {product?.label?.length > 0 && (
        <div className="absolute z-10 flex items-center justify-center max-w-full gap-2 px-3 py-px top-3 left-20">
          {product?.label?.map(
            (label, i) =>
              label?.status === "1" &&
              label?.["frontend_settings"]?.image && (
                <NextImage
                  key={i}
                  src={validateImgSrc(
                    process.env.NEXT_PUBLIC_IMG_BASEURL +
                      "/amasty/amlabel/" +
                      label?.["frontend_settings"]?.image
                  )}
                  style={{
                    height: label?.["frontend_settings"]?.image_size + "px",
                  }}
                  priority
                  alt={label?.name}
                  width={label?.["frontend_settings"]?.image_size || 100}
                  height={label?.["frontend_settings"]?.image_size || 50}
                  className="w-full object contain"
                />
              )
          )}
        </div>
      )}
      <div className="w-full">
        <NextImage
          src={validateImgSrc(
            previewImg ||
              images.at(0)?.image ||
              `${process.env.NEXT_PUBLIC_IMG_BASEURL}/catalog/product/${product?.image}` ||
              "/imgs/normal-img.png"
          )}
          width={545}
          height={384}
          className={`object-contain rounded-md size-full max-sm:hidden sm:max-h-96 ${
            textLabels?.length > 0 && "mt-5"
          }`}
          priority
          alt="Main Img !"
        />

        {textLabels?.length > 0 && (
          <p className="truncate bg-green-100 text-[10px] sm:text-sm py-1 px-2 w-fit flex items-center gap-2 absolute top-0 left-1/2 -translate-x-1/2 z-40">
            <InfoIcon />
            {textLabels?.[0]?.["frontend_settings"]?.label_text}
          </p>
        )}
        <div className="absolute z-30 block top-3 left-3 sm:hidden">
          <Share
            label={<ShareIcon className="cursor-pointer size-4" />}
            title={product?.name || ""}
            description={product?.meta_description || ""}
          />
        </div>
        <div className="absolute z-30 sm:top-1 right-3">
          <AddtoWishlist
            fromAlbum
            isWishlisted={product?.sku in wishlist}
            onClick={() => {
              product?.sku in wishlist
                ? event_view_item(product, "remove_from_wishlist")
                : event_view_item(product, "add_to_wishlist");
              handleWishList(wishlist, product, dispatch);
            }}
          />{" "}
          <div className="hidden max-sm:block">
            <AddToCompare
              product={product}
              t={t}
              iconClassName={
                "mb-1 absolute right-3 top-10 size-5 text-secondary-800"
              }
              fromPDP
            />
          </div>
        </div>
        <div className="absolute z-30 cursor-pointer max-sm:bottom-3 sm:top-3 left-3">
          <ZoomInIcon
            onClick={() => setOpenFancybox(true)}
            className="size-6 sm:size-8"
          />
        </div>
      </div>

      {/* images */}
      <div className="items-center justify-center hidden w-full gap-5 px-1 overflow-x-auto sm:flex">
        {/* sub images */}
        <Swiper
          breakpoints={{
            320: {
              slidesPerView: 1,
              loop: true,
              pagination: { clickable: true, enabled: true },
            },
            641: {
              slidesPerView: 3,
              // loop: false,
              // enabled: false,
            },
            769: {
              slidesPerView: 2.7,
              // loop: false,
              // enabled: false,
            },
            901: {
              slidesPerView: 3,
              // loop: false,
              // enabled: false,
            },
            1023: {
              slidesPerView: 4,
              // loop: false,
              // enabled: false,
            },
            1200: {
              slidesPerView: 5,
              spaceBetween: 0,
              // loop: false,
              // enabled: false,
            },
            1400: {
              slidesPerView: 6,
              spaceBetween: 0,
              // loop: false,
              // enabled: false,
            },
          }}
          // pagination={{ clickable: true }}
          modules={[Pagination]}
          // className="block max-sm:pb-9 sm:mt-3 banner-swiper sm:hidden"
          className="items-center justify-center hidden w-full gap-5 px-1 sm:flex"
        >
          {images?.map((img, idx) => (
            <SwiperSlide key={idx}>
              <div className="relative h-24 overflow-hidden aspect-square sm:w-24">
                <NextImage
                  key={idx}
                  onClick={() => setPreviewImg(img?.image)}
                  // priority
                  // width={96}
                  // height={96}
                  fill
                  loading="lazy"
                  src={validateImgSrc(img?.image)}
                  className="object-contain cursor-pointer size-full"
                  alt="Sub Img 1!"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* responsive images */}
      <Swiper
        breakpoints={{
          320: {
            slidesPerView: 1,
            loop: true,
            pagination: { clickable: true, enabled: true },
          },
          641: {
            slidesPerView: 5,
            loop: false,
            enabled: false,
          },
          769: {
            slidesPerView: 2.7,
            loop: false,
            enabled: false,
          },
          901: {
            slidesPerView: 3,
            loop: false,
            enabled: false,
          },
          1023: {
            slidesPerView: 3.5,
            loop: false,
            enabled: false,
          },
          1200: {
            slidesPerView: 4.5,
            loop: false,
            enabled: false,
          },
        }}
        pagination={{ clickable: false }}
        modules={[Pagination]}
        className="block max-sm:pb-9 sm:mt-3 banner-swiper sm:hidden"
      >
        {images?.map((img) => (
          <SwiperSlide key={uuidv4()}>
            <div className="relative overflow-hidden aspect-square max-sm:h-52 sm:w-24">
              <NextImage
                onClick={() => setPreviewImg(img?.image)}
                src={validateImgSrc(img?.image)}
                fill
                
                className={`object-contain w-full p-2 rounded-md cursor-pointer sm:border  ${
                  textLabels?.length > 0 && "mt-6"
                }`}
                alt="Sub Img 1!"
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {openFancybox ? (
        <ImageLightBox
          images={images}
          open={openFancybox}
          close={setOpenFancybox}
        />
      ) : null}
    </div>
  );
};

export default Album;
